import { gql, useMutation } from '@apollo/client';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import * as React from 'react';

import { getStripeJs } from '../../../lib/stripeJs';

import CardEntryBase from './CardEntryBase';

import type { StartCardSetupMutation, StartCardSetupMutationVariables } from './__generated__/CardEntry.gql';

const START_CARD_SETUP = gql`
  mutation StartCardSetup {
    startCardSetup {
      clientSecret
      setupIntentId
    }
  }
`;

interface ICardEntryFormProps extends ICardEntryProps {
  setupIntentId: string;
}

/**
 * Inside `Elements` so the Stripe hooks resolve; confirms the setup the
 * browser-held element collected. `redirect: 'if_required'` keeps
 * non-redirecting methods (cards, wallets) on this page.
 */
const CardEntryForm = ({ onConfirmed, onBack, setupIntentId }: ICardEntryFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
  const [elementReady, setElementReady] = React.useState(false);

  const handleSubmit = async () => {
    if (stripe === null || elements === null) {
      return;
    }
    setIsSubmitting(true);
    setErrorMessage(undefined);
    const result = await stripe.confirmSetup({ elements, redirect: 'if_required' });
    setIsSubmitting(false);
    if (result.error !== undefined) {
      setErrorMessage(result.error.message ?? 'Your card could not be saved. Try again.');
      return;
    }
    onConfirmed(result.setupIntent?.id ?? setupIntentId);
  };

  return (
    <CardEntryBase
      paymentElement={
        <div className={elementReady ? undefined : 'min-h-24'}>
          <PaymentElement
            onReady={() => setElementReady(true)}
            options={{
              // Link's instant-bank option is a dead end on this card-only
              // rail — see the README for the full constraint.
              wallets: { link: 'never' },
            }}
          />
        </div>
      }
      canSubmit={stripe !== null && elements !== null && elementReady}
      onSubmit={() => void handleSubmit()}
      onBack={onBack}
      isSubmitting={isSubmitting}
      errorMessage={errorMessage}
    />
  );
};

export interface ICardEntryProps {
  /** Called with the confirmed setup's id; the subscribe names it. */
  onConfirmed: (setupIntentId: string) => void;
  onBack: () => void;
}

/**
 * Opens a card setup and mounts the Payment Element against it.
 *
 * The mutation runs once on mount: a fresh setup per visit is deliberate —
 * the server keys it per call, so a declined card retried from scratch is
 * never handed back the setup that already failed.
 */
const CardEntry = ({ onConfirmed, onBack }: ICardEntryProps) => {
  const [startCardSetup, { data, error }] = useMutation<StartCardSetupMutation, StartCardSetupMutationVariables>(
    START_CARD_SETUP
  );
  const [stripeLoadFailed, setStripeLoadFailed] = React.useState(false);
  // Elements never settles a rejected promise, so a load failure has to be
  // caught here and turned into the error screen; the null keeps the promise
  // in the shape Elements accepts.
  const stripePromise = React.useMemo(
    () =>
      getStripeJs().catch(() => {
        setStripeLoadFailed(true);
        return null;
      }),
    []
  );

  const started = React.useRef(false);
  React.useEffect(() => {
    if (!started.current) {
      started.current = true;
      // The mutate function also rejects on a transport failure; the hook's
      // `error` state below is what renders it, so swallow the rejection.
      void startCardSetup().catch(() => undefined);
    }
  }, [startCardSetup]);

  if (error !== undefined || stripeLoadFailed) {
    return (
      <CardEntryBase
        paymentElement={null}
        canSubmit={false}
        onSubmit={() => undefined}
        onBack={onBack}
        isSubmitting={false}
        errorMessage={
          stripeLoadFailed
            ? 'We could not load the payment form. Go back and try again.'
            : 'We could not start card setup. Go back and try again.'
        }
      />
    );
  }

  const setup = data?.startCardSetup;
  if (setup == null) {
    return (
      <CardEntryBase
        paymentElement={null}
        canSubmit={false}
        onSubmit={() => undefined}
        onBack={onBack}
        isSubmitting={false}
      />
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret: setup.clientSecret }}>
      <CardEntryForm onConfirmed={onConfirmed} onBack={onBack} setupIntentId={setup.setupIntentId} />
    </Elements>
  );
};

CardEntry.displayName = 'CardEntry';

export default CardEntry;
